import React, { useMemo, useState } from 'react';

import { Flex, List, Modal } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Select, Text } from 'scouting-ui/lib';

import { useMeritBadgeCounselorProfile } from '@modules/meritBadgeCounselors/hooks';
import { useGenerateClassWorksheetMutation } from '@modules/rtk/webscript-api';
import { PersonAvatar } from '@shared/components';
import { intl } from '@shared/localization';
import { getPreferredName } from '@shared/utils/preferredName';
import { toastService } from '@toasts';

import { ViewByTabs } from '../duck/constants';
import {
  dashboardViewBySel,
  dashboardYouthsByMIDSel,
  modalSel,
  selectedYouthByMBSel,
  selectedYouthSel,
} from '../duck/selectors';
import { mbcDashboardActions } from '../duck/slice';
import { YouthByMBRowKey } from '../duck/utils';
import MBImage from './MBImage';

interface Props {
  mbcMemberId: string | number;
}

export default function GenerateClassWorksheetModal({ mbcMemberId }: Props) {
  const open = useSelector(modalSel('classWorksheet'));
  const selectedYouthIds = useSelector(selectedYouthSel);
  const selectedYouthByMB = useSelector(selectedYouthByMBSel);
  const viewBy = useSelector(dashboardViewBySel);
  const youthsByMID = useSelector(dashboardYouthsByMIDSel);
  const dispatch = useDispatch();
  const [selectedMB, setSelectedMB] = useState<number | null>(null);
  const [generateClassWorksheet, { isLoading: isGenerating }] =
    useGenerateClassWorksheetMutation();

  const { meritBadges, loading: loadingProfile } =
    useMeritBadgeCounselorProfile({
      memberId: mbcMemberId as string,
      extendedInfo: true,
    });

  // Parse MB view selections to extract member IDs and merit badge IDs
  const parsedMBSelections = useMemo(() => {
    if (viewBy !== ViewByTabs.MeritBadge) return null;
    const parsed = selectedYouthByMB.map(YouthByMBRowKey.parseKey);
    const uniqueMemberIds = [...new Set(parsed.map(p => p.memberId))];
    const uniqueMBIds = [...new Set(parsed.map(p => p.meritBadgeId))];
    return { memberIds: uniqueMemberIds, meritBadgeIds: uniqueMBIds };
  }, [viewBy, selectedYouthByMB]);

  // Pre-select MB when all selections share the same merit badge
  const preSelectedMB =
    parsedMBSelections?.meritBadgeIds.length === 1
      ? parsedMBSelections.meritBadgeIds[0]
      : null;

  // Effective member IDs based on current view
  const effectiveYouthIds = parsedMBSelections
    ? parsedMBSelections.memberIds
    : selectedYouthIds;

  const closeModal = () => {
    dispatch(
      mbcDashboardActions.setModal({ modal: 'classWorksheet', open: false }),
    );
    setSelectedMB(null);
  };

  const handleGenerate = async () => {
    try {
      const mbId = selectedMB ?? preSelectedMB;
      const blob = await generateClassWorksheet({
        memberIds: effectiveYouthIds.map(Number),
        meritBadgeId: String(mbId),
      }).unwrap();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toastService.error('Failed to generate worksheet. Please try again.');
    }
  };

  const mbOptions = useMemo(
    () =>
      meritBadges.map(badge => ({
        label: badge.meritBadgeName,
        value: badge.meritBadgeId,
        image: badge.imageUrl100,
      })),
    [meritBadges],
  );

  const selectedYouths = useMemo(
    () => effectiveYouthIds.map(id => youthsByMID[id]).filter(Boolean),
    [effectiveYouthIds, youthsByMID],
  );

  return (
    <Modal
      width={600}
      open={open}
      destroyOnClose
      title={<FormattedMessage id="mbcDashboard.classWorksheetModal.title" />}
      onCancel={closeModal}
      onClose={closeModal}
      footer={
        <Flex justify="flex-end" gap="small">
          <Button type="default" onClick={closeModal}>
            <FormattedMessage id="shared.cancel" />
          </Button>
          <Button
            type="primary"
            disabled={!selectedMB && !preSelectedMB}
            loading={isGenerating}
            onClick={handleGenerate}
          >
            <FormattedMessage id="mbcDashboard.classWorksheetModal.generate" />
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap="middle">
        <Select
          showSearch
          placeholder={intl.formatMessage({
            id: 'mbcDashboard.classWorksheetModal.selectMB',
          })}
          loading={loadingProfile}
          optionFilterProp="label"
          options={mbOptions}
          value={selectedMB ?? preSelectedMB}
          onChange={value => setSelectedMB(value)}
          optionRender={option => (
            <Flex align="center" gap={4}>
              <MBImage height="auto" width={24} src={option.data.image} />
              {option.data.label}
            </Flex>
          )}
        />

        <div>
          <Text font="body-body-2" strong>
            <FormattedMessage id="mbcDashboard.classWorksheetModal.selectedYouth" />
          </Text>
          <List
            size="small"
            dataSource={selectedYouths}
            renderItem={youth => (
              <List.Item key={youth.memberId}>
                <Flex align="center" gap="small">
                  <PersonAvatar isAdult={false} gender={youth.gender} />
                  <Text>{getPreferredName(youth)}</Text>
                </Flex>
              </List.Item>
            )}
          />
        </div>
      </Flex>
    </Modal>
  );
}
